// src/features/analytics/useAnalyticsLive.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi, type AnalyticsViewScope } from './AnalyticsApi';
import {
  ANALYTICS_LIVE_POLL_INTERVAL_MS,
  ANALYTICS_QUERY_KEYS,
} from '@/lib/constants/analytics';
import type { AnalyticsEnvelope, LiveData } from './types';

export interface UseAnalyticsLiveResult {
  live: AnalyticsEnvelope<LiveData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Live concurrency. Polls exactly once per 60 s while mounted and visible
 * (TanStack default refetchIntervalInBackground:false keeps hidden tabs
 * quiet). Mount only where the value is shown (ADR-003 §7).
 */
export function useAnalyticsLive(scope: AnalyticsViewScope | null): UseAnalyticsLiveResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.live(scope?.kind ?? 'account', scope?.cid ?? 0),
    queryFn: () => AnalyticsApi.getLive(scope!),
    enabled: scope !== null,
    staleTime: 0,
    refetchInterval: ANALYTICS_LIVE_POLL_INTERVAL_MS,
  });

  return {
    live: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
